import {
  cn,
  Icon,
  IconButton,
  Separator,
  Spinner,
  Text,
} from '@risksmart-app/atomic-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ActionMenu } from './parts/ActionMenu';
import { FadeIn } from './parts/FadeIn';
import { FadeTitle } from './parts/FadeTitle';
import { PrimaryActionButton } from './parts/PrimaryActionButton';
import type { RevealMode } from './parts/RevealButton';
import { RevealButton } from './parts/RevealButton';
import type {
  ObjectToolbarProps,
  ToolbarAction,
  ToolbarActionGroup,
  ToolbarOverflow,
} from './types';
import { ICON_BUTTON_SIZE } from './types';

/** Stable id for the overflow "Edit" group that hosts Toolbar settings. */
const OVERFLOW_EDIT_ID = 'edit';

/**
 * The synthesised overflow section headings + the Toolbar-settings item label —
 * every one fed from i18n by the connected {@link ObjectToolbarView} so no
 * English string is hardcoded in the toolbar.
 */
interface OverflowLabels {
  edit: string;
  download: string;
  dangerZone: string;
  toolbarSettings: string;
}

/**
 * Merge the "Toolbar settings" action into the overflow's "Edit" group so every
 * toolbar exposes it. If the caller already declares an Edit group (e.g. the
 * relocated ribbon "Filter settings" action) the settings item is appended to
 * it; otherwise a new Edit group is prepended so it leads the menu.
 */
const withToolbarSettings = (
  groups: ToolbarActionGroup[],
  settingsAction: ToolbarAction,
  editLabel: string
): ToolbarActionGroup[] => {
  const editIndex = groups.findIndex(
    (group) => group.id === OVERFLOW_EDIT_ID || group.label === editLabel
  );

  if (editIndex === -1) {
    return [
      {
        id: OVERFLOW_EDIT_ID,
        label: editLabel,
        actions: [settingsAction],
      },
      ...groups,
    ];
  }

  return groups.map((group, index) =>
    index === editIndex
      ? { ...group, actions: [...group.actions, settingsAction] }
      : group
  );
};

/**
 * Flatten the overflow config into ordered, labelled groups. Caller `groups`
 * render first (in order), then the synthesised "Download" group (second-last),
 * then the "Danger zone" group (last). Danger items are forced to the
 * `destructive` variant. When `settingsAction` is supplied it is merged into the
 * "Edit" group (see {@link withToolbarSettings}) — this is what puts the Toolbar
 * settings opener on every toolbar. Section headings come from `labels` (i18n).
 */
const buildOverflowGroups = (
  { groups = [], download, danger }: ToolbarOverflow,
  labels: OverflowLabels,
  settingsAction?: ToolbarAction
): ToolbarActionGroup[] => {
  const leadingGroups = settingsAction
    ? withToolbarSettings(groups, settingsAction, labels.edit)
    : groups;

  return [
    ...leadingGroups,
    ...(download && download.length > 0
      ? [
          {
            id: 'download',
            label: labels.download,
            actions: download,
          },
        ]
      : []),
    ...(danger && danger.length > 0
      ? [
          {
            id: 'danger',
            label: labels.dangerZone,
            actions: danger.map((action) => ({
              ...action,
              variant: 'destructive' as const,
            })),
          },
        ]
      : []),
  ];
};

/**
 * Pure, controlled object-level toolbar. Renders the object header on the left
 * (title + optional page count) and a right-aligned action tray on the right.
 *
 * Tray order (left → right):
 *   primary icon buttons → Save disc (or Approve tick) → Cancel X →
 *   divider → overflow ⋮
 *
 * Save and Approve are mutually exclusive: when `cancel.mode === 'cancel'`
 * (or cancel is absent) the Save disc shows; `cancel.mode === 'approve-cancel'`
 * (TPP > Response) shows the Approve tick and hides Save.
 *
 * The X button only renders when `save` is present (or `approve-cancel` mode)
 * and always calls `navigate(-1)` — no `onCancel` prop needed.
 */
const ObjectToolbarView = ({
  header,
  save,
  cancel,
  actions = {},
  primaryTitleSlideout = false,
  showActionLabels = true,
  onOpenSettings,
}: ObjectToolbarProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const overflowLabels: OverflowLabels = {
    edit: t('objectToolbar.edit'),
    download: t('objectToolbar.download'),
    dangerZone: t('objectToolbar.dangerZone'),
    toolbarSettings: t('objectToolbar.toolbarSettings'),
  };

  // The "Toolbar settings" opener is injected into the overflow "Edit" group
  // whenever the connected toolbar provides `onOpenSettings`, so it appears on
  // every toolbar regardless of the caller's action config.
  const settingsAction: ToolbarAction | undefined = onOpenSettings
    ? {
        id: 'toolbar-settings',
        label: overflowLabels.toolbarSettings,
        iconName: 'settings-01',
        onClick: onOpenSettings,
      }
    : undefined;

  const overflow = buildOverflowGroups(
    actions.overflow ?? {},
    overflowLabels,
    settingsAction
  );

  const hasOverflow = overflow.some((group) => group.actions.length > 0);

  // Every tray button shares one reveal treatment: no label reveal when the
  // user turns labels off, otherwise slideout or tooltip per the picker. Slideout
  // only applies while labels are shown.
  const useSlideout = showActionLabels && primaryTitleSlideout;
  const reveal: RevealMode = !showActionLabels
    ? 'none'
    : primaryTitleSlideout
      ? 'slideout'
      : 'tooltip';

  // Which leading tray controls to show. Save and Approve are mutually exclusive:
  // approve-cancel mode (TPP > Response) shows the Approve tick and hides Save;
  // otherwise the Save disc shows when `save` is present. The Cancel X shows
  // whenever the Save disc shows or in approve-cancel mode.
  const isApproveCancelMode = cancel?.mode === 'approve-cancel';
  const showSaveButton = !isApproveCancelMode && !!save;
  const showApproveButton = isApproveCancelMode;
  const showCancelButton = !!cancel && (isApproveCancelMode || !!save);

  // Assemble the leading action tray (everything to the left of the overflow ⋮),
  // spreading each control in only when it should show.
  // Each tray control is wrapped in <FadeIn> so a control appearing when the
  // tab's data loads in (the tray is rebuilt as the active form publishes its
  // config) fades in rather than popping. Controls already present keep their
  // React key and never re-fade.
  const trayButtons: ReactNode[] = [
    ...(actions.primary ?? []).map((action) => (
      <FadeIn key={action.id}>
        <PrimaryActionButton action={action} reveal={reveal} />
      </FadeIn>
    )),
    ...(showSaveButton
      ? [
          <FadeIn key={'save'}>
            <RevealButton
              label={'Save'}
              reveal={reveal}
              disabled={save?.disabled || save?.loading}
              onClick={() => save?.onSave()}
            >
              {save?.loading ? (
                <Spinner size={'md'} />
              ) : (
                <Icon
                  name={'save-01'}
                  size={'lg'}
                  className={cn(
                    save?.dirty && !save?.disabled && 'animate-save-pulse'
                  )}
                />
              )}
            </RevealButton>
          </FadeIn>,
        ]
      : []),
    ...(showApproveButton
      ? [
          <FadeIn key={'approve'}>
            <RevealButton
              label={'Approve'}
              reveal={reveal}
              onClick={() => cancel?.onApprove?.()}
            >
              <Icon name={'check-2'} size={'lg'} />
            </RevealButton>
          </FadeIn>,
        ]
      : []),
    ...(showCancelButton
      ? [
          <FadeIn key={'cancel'}>
            <RevealButton
              label={'Cancel'}
              reveal={reveal}
              onClick={() => navigate(-1)}
              disabled={save?.loading}
            >
              <Icon name={'x'} size={'lg'} />
            </RevealButton>
          </FadeIn>,
        ]
      : []),
  ];

  // The vertical divider only makes sense between leading tray items and the
  // overflow ⋮ — skip it when either side is absent.
  const hasLeadingTrayItems = trayButtons.length > 0;

  return (
    <div
      className={cn(
        'bg-neutral border-neutral-border-minimal sticky top-0 z-10 flex flex-row items-center justify-between gap-2 border-b p-4'
      )}
    >
      <div className={cn('flex min-w-0 flex-1 items-center gap-2')}>
        <FadeTitle title={header.title} />
        {header.counter ? (
          <Text
            preset={'heading-md'}
            variant={'muted'}
            className={cn('shrink-0 whitespace-nowrap font-light')}
          >
            {header.counter}
          </Text>
        ) : null}
      </div>

      {/* Slideout mode uses gap-0: the tray buttons sit edge-to-edge so there is
          no non-hoverable strip between adjacent icons. That dead-zone is what
          made the slideout label dip when the pointer crossed between buttons —
          over the gap no button matched :hover, so the open label collapsed.
          Contiguous buttons hand :hover over on a single frame; icon spacing is
          carried by each button's own padding, and the overflow Separator
          restores its own spacing via mx-1. Tooltip mode (flag off) has no
          reveal reflow, so it keeps the original gap-1 spacing. */}
      <div
        className={cn(
          'flex shrink-0 items-center',
          useSlideout ? 'gap-0' : 'gap-1'
        )}
      >
        {trayButtons}

        {hasOverflow ? (
          <>
            {hasLeadingTrayItems ? (
              <Separator
                orientation={'vertical'}
                className={cn(
                  'h-8 bg-neutral-border-minimal',
                  // gap-0 slideout tray: restore the separator's own spacing.
                  // Tooltip mode keeps its spacing from the tray's gap-1.
                  useSlideout && 'mx-1'
                )}
              />
            ) : null}
            <FadeIn key={'overflow'}>
              <ActionMenu
                groups={overflow}
                trigger={
                  <IconButton
                    aria-label={'More actions'}
                    className={cn(ICON_BUTTON_SIZE)}
                  >
                    <Icon name={'dots-vertical'} size={'lg'} />
                  </IconButton>
                }
              />
            </FadeIn>
          </>
        ) : null}
      </div>
    </div>
  );
};

export { ObjectToolbarView };
